//////////////////
// IMPORTS
//////////////////
// schema
import * as ISchema from "@schema";
// libraries
import * as React from "react";
import { connect } from "react-redux";
import { createRefetchContainer } from "react-relay";
// org-invite imports
import { fragments, refetchQuery } from "./OrgInvite.gql";
import { IContainer, IProps, IState, AugmentedPersonMap } from "./OrgInvite.d";
import * as s from "./OrgInvite.styles";
// other internals
import { InviteCard } from "@components/invite-card";
import { CopyPersonalInviteLinkBtn } from "@components/copy-personal-invite-link-btn";
import { IReduxState } from "@reducers/root.reducer";
import { CircularProgress } from "@clockwise/web-commons/src/components/circular-progress";
import { sendInvite } from "@actions/settings.actions";
import { updateOrgInvite } from "@mutations";
import { H2 } from "@ui";
import { TrackingEvents, track } from "@util/analytics.util";
import { CalloutBox } from "@components/callout-box";

// material-ui
import { InputAdornment, List, TextField, IconButton } from "@material-ui/core";
import { Search, Close, Check } from "@material-ui/icons";
import withStyles from "@material-ui/core/styles/withStyles";

//////////////////
// COMPONENT
//////////////////
export class OrgInviteComponent extends React.Component<IProps, IState> {
  public static ClipCount = 5;

  constructor(props: IProps) {
    super(props);

    const augmentedPersons = this.getAugmentedPersons(props, "");
    const currentPersons = this.getSortedPersons(augmentedPersons);
    this.state = {
      currentPersons,
      recentlySentPersons: {},
      query: "",
      lastProcessedQuery: "",
      loading: this.isLoading(props),
      fastInviteLoading: false,
      fastInviteSuccess: "",
      fastInviteFailure: "",
    };
  }

  private isLoading = (props: IProps) => {
    return (
      !props.org.personCorpusErrorable ||
      props.org.personCorpusErrorable.__typename !== "PersonList" ||
      !props.org.personCorpusErrorable.list.length
    );
  };

  public componentDidMount() {
    if (this.props.pendingSendInvite) {
      const email = this.props.pendingSendInvite;
      const calendarIds = [email];
      this.setState({ fastInviteLoading: true });
      updateOrgInvite(
        this.props.relay.environment,
        { calendarIds, orgRelayId: this.props.org.id },
        () => this.onFastInviteSuccess(email),
        () => this.onFastInviteFailure(email),
      );
      this.props.sendInvite("");
      track(TrackingEvents.SETTINGS.SEND_INVITE_FROM_URL, {
        calendarId: email,
      });
    }
  }

  private onFastInviteSuccess = (email: string) => {
    this.setState({ fastInviteSuccess: email, fastInviteLoading: false });
    this.inviteCallback(email);
  };

  private onFastInviteFailure = (email: string) => {
    this.setState({ fastInviteFailure: email, fastInviteLoading: false });
  };

  private closeFastInviteConfirmation = () => {
    this.setState({ fastInviteSuccess: "", fastInviteFailure: "" });
  };

  public UNSAFE_componentWillReceiveProps(nextProps: IProps) {
    const { query, loading, lastProcessedQuery } = this.state;
    const newState: Partial<IState> = {};
    if (query !== lastProcessedQuery) {
      const augmentedPersons = this.getAugmentedPersons(nextProps, query);
      newState.currentPersons = this.getSortedPersons(augmentedPersons);
      newState.lastProcessedQuery = this.state.query;
    }
    if (loading && !this.isLoading(nextProps)) {
      newState.loading = false;
    }
    this.setState(newState as IState);
  }

  private inviteCallback = (calId: string) => {
    // keep track of recently sent persons so they can persist on the unfiltered view
    const { recentlySentPersons, currentPersons } = this.state;
    const person = currentPersons.find((p) => p.targetCalendarId === calId);
    if (person) {
      person.isPending = true;
      recentlySentPersons[calId] = person;
      this.setState({ currentPersons, recentlySentPersons });
    }

    track(TrackingEvents.SETTINGS.SEND_INVITE, { calendarId: calId });
  };

  private getAugmentedPersons = (props: IProps, query: string) => {
    const org = props.org;
    const suggestedInviteMap = this.getSuggestedInviteMap(props);
    const pendingInviteMap = this.getPendingInviteMap(props);
    const recentlySentPersons = (this.state && this.state.recentlySentPersons) || {};

    // sanity check
    if (!org || !org.suggestedInvites || !org.suggestedInvites.edges) {
      return {} as AugmentedPersonMap;
    }

    // use either query result or corpus
    let persons: ISchema.IPerson[] = [];
    if (query) {
      const personQuery = props.viewer.personQueryResultErrorable;
      if (personQuery.__typename !== "PersonQueryResult") {
        return {} as AugmentedPersonMap;
      }
      persons = personQuery.persons;
    } else {
      const personCorpus = props.org.personCorpusErrorable;
      if (personCorpus.__typename !== "PersonList") {
        return {} as AugmentedPersonMap;
      }
      persons = personCorpus.list;
    }

    const result: AugmentedPersonMap = persons
      .filter((person) => {
        if (!this.props.showCurrentUser && person.isYou) {
          return false;
        }
        return true;
      })
      .reduce((accumulator: AugmentedPersonMap, nextPerson): AugmentedPersonMap => {
        const { profile, primaryEmail, isYou, isUser } = nextPerson;
        const isRecentlySent = !!recentlySentPersons[primaryEmail];
        const isPending = !!pendingInviteMap[primaryEmail] || isRecentlySent;

        // defaults
        accumulator[primaryEmail] = {
          isYou,
          isUser,
          isPending,
          targetCalendarId: primaryEmail,
          givenName: profile ? profile.givenName : primaryEmail, // if no user profile, show primaryEmail
          familyName: profile ? profile.familyName : "",
          externalImageUrl: profile ? profile.externalImageUrl : "",
          isSuggested: false,
          recentlySent: isRecentlySent,
          numberOfSharedMeetings: 0,
          numberOfOrganizedMeetings: 0,
          inviteReason: ISchema.SuggestedOrgInviteReason.AttendSameMeetings,
          totalScore: 0,
        };

        // handle suggested invites
        const suggestedInvite = suggestedInviteMap[primaryEmail];
        if (!!suggestedInvite) {
          accumulator[primaryEmail].isSuggested = true;
          accumulator[primaryEmail].numberOfSharedMeetings = suggestedInvite.numberOfSharedMeetings;
          accumulator[primaryEmail].numberOfOrganizedMeetings =
            suggestedInvite.numberOfOrganizedMeetings;
          accumulator[primaryEmail].totalScore = suggestedInvite.totalScore;
          accumulator[primaryEmail].inviteReason = suggestedInvite.inviteReason;
        }

        return accumulator;
      }, {});

    // include any recently sent person if there's no query
    if (!query) {
      Object.keys(recentlySentPersons).forEach((cId: string) => {
        result[cId] = recentlySentPersons[cId];
      });
    }

    return result;
  };

  private getSuggestedInviteMap = (
    props: IProps,
  ): { [calId: string]: ISchema.ISuggestedOrgInvite } => {
    const suggestedInvites = props.org.suggestedInvites;
    const suggestedInviteMap: { [calId: string]: ISchema.ISuggestedOrgInvite } = {};
    if (!suggestedInvites || !suggestedInvites.edges) {
      return suggestedInviteMap;
    }

    suggestedInvites.edges.forEach((edge) => {
      suggestedInviteMap[edge.node.targetCalendarId] = edge.node;
    });

    return suggestedInviteMap;
  };

  private getPendingInviteMap = (props: IProps): { [calId: string]: ISchema.IOrgInvite } => {
    const pendingInvites = props.org.pendingInvites;
    const pendingInviteMap: { [calId: string]: ISchema.IOrgInvite } = {};
    if (!pendingInvites || !pendingInvites.edges) {
      return pendingInviteMap;
    }

    pendingInvites.edges.forEach((edge) => {
      pendingInviteMap[edge.node.targetCalendarId] = edge.node;
    });

    return pendingInviteMap;
  };

  private onSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const query = event.target.value;
    this.props.relay.refetch({
      orgRelayId: this.props.org.id,
      queryInput: query,
      excludeCalendars: [],
    });
    this.setState({ query });
  };

  public renderPersons = () => {
    const { currentPersons, recentlySentPersons } = this.state;

    if (!currentPersons.length) {
      return <div className={this.props.classes.empty}>No results found</div>;
    }

    return currentPersons.map((p) => {
      const isRecentlySent = !!recentlySentPersons[p.targetCalendarId];
      return (
        <InviteCard
          key={p.targetCalendarId}
          org={this.props.org}
          invitee={p}
          inviteCallback={this.inviteCallback.bind(this)}
          recentlySent={isRecentlySent}
        />
      );
    });
  };

  private getSortedPersons(augmentedPersons: AugmentedPersonMap) {
    if (!Object.keys(augmentedPersons)) {
      return [];
    }

    return Object.keys(augmentedPersons)
      .sort((a, b) => {
        const personA = augmentedPersons[a];
        const personB = augmentedPersons[b];
        if (personA.isYou) {
          return 1;
        } else if (personB.isYou) {
          return -1;
        } else if (personA.isUser) {
          return 1;
        } else if (personB.isUser) {
          return -1;
        } else if (personA.isPending && !personB.isPending) {
          return 1;
        } else if (personB.isPending && !personA.isPending) {
          return -1;
        } else if (personA.isSuggested && personB.isSuggested) {
          return personB.totalScore - personA.totalScore;
        } else if (personA.isSuggested) {
          return -1;
        } else if (personB.isSuggested) {
          return 1;
        } else if (personA.givenName < personB.givenName) {
          return -1;
        } else if (personB.givenName < personA.givenName) {
          return 1;
        } else if (personA.familyName < personB.familyName) {
          return -1;
        } else if (personB.familyName < personA.familyName) {
          return 1;
        }

        return 0;
      })
      .slice(0, 20) // for perf reasons, only render 20
      .map((k) => augmentedPersons[k]);
  }

  public render() {
    const { fastInviteSuccess, fastInviteFailure, fastInviteLoading, loading } = this.state;
    const { org, viewer } = this.props;

    if (loading) {
      return <CircularProgress />;
    }

    const marginCorrect = !!this.props.marginCorrect
      ? {
          marginLeft: this.props.marginCorrect,
          marginRight: this.props.marginCorrect,
        }
      : {};

    return (
      <div>
        {fastInviteLoading ? (
          <div style={s.confirmation.container}>
            <div style={s.confirmation.row}>
              <H2 style={s.confirmation.h2}>Sending invite...</H2>
            </div>
          </div>
        ) : null}
        {fastInviteSuccess ? (
          <div style={s.confirmation.container}>
            <div style={s.confirmation.row}>
              <Check {...s.confirmation.check} />
              <H2 style={s.confirmation.h2}>Invited {fastInviteSuccess} to join Clockwise!</H2>
              <IconButton style={s.close} onClick={() => this.closeFastInviteConfirmation()}>
                <Close {...s.icon} />
              </IconButton>
            </div>
          </div>
        ) : null}
        {fastInviteFailure ? (
          <div style={s.confirmation.container}>
            <CalloutBox error>
              Sorry, there was a problem inviting {fastInviteFailure}. Please try again.
            </CalloutBox>
          </div>
        ) : null}
        <div className={this.props.classes.container}>
          <TextField
            label="Search for teammates"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search className={this.props.classes.searchField} />
                </InputAdornment>
              ),
            }}
            value={this.state.query}
            onChange={this.onSearch}
            classes={{ root: this.props.classes.textField }}
          />
          <CopyPersonalInviteLinkBtn
            className={this.props.classes.linkButton}
            org={org}
            user={viewer.user}
          />
        </div>
        <List className={this.props.classes.list} style={{ ...marginCorrect }}>
          {this.renderPersons()}
        </List>
      </div>
    );
  }
}

//////////////////
// WITH STYLES
//////////////////
const OrgInviteStyled = withStyles(s.styles)(OrgInviteComponent);

//////////////////
// REDUX
//////////////////
function mapStateToProps(state: IReduxState, _ownProps: IContainer) {
  return {
    pendingSendInvite: state.settings.pendingSendInvite,
  }; // add redux state here, if needed
}

const reduxContainer = connect(mapStateToProps, {
  sendInvite,
})(OrgInviteStyled);

//////////////////
// RELAY
//////////////////
export const OrgInvite = createRefetchContainer(reduxContainer, fragments, refetchQuery);
